import React, { useState, useEffect, useCallback, useRef } from 'react';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  GitCompare, User, Target, Swords, Activity, Brain,
  ShieldCheck, CalendarCheck, HeartPulse, Save,
  Printer, ArrowUpRight, ArrowDownRight, Minus
} from 'lucide-react';
import { toast } from 'sonner';
import SearchableAthleteSelect from '../components/SearchableAthleteSelect';

const API = process.env.REACT_APP_BACKEND_URL;

const CompareAthletes = () => {
  const [athletes, setAthletes] = useState([]);
  const [selectedA, setSelectedA] = useState(null);
  const [selectedB, setSelectedB] = useState(null);
  const [dataA, setDataA] = useState(null);
  const [dataB, setDataB] = useState(null);
  const [loading, setLoading] = useState(false);
  const [notes, setNotes] = useState([]);
  const [newNote, setNewNote] = useState('');
  const [savingNote, setSavingNote] = useState(false);
  const printRef = useRef();

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    fetch(`${API}/api/athletes`, { headers })
      .then(r => r.json())
      .then(data => setAthletes(Array.isArray(data) ? data : []))
      .catch(() => {});
  }, []);

  const fetchAthleteData = useCallback(async (id, setter) => {
    if (!id) { setter(null); return; }
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/compare/athlete-data/${id}`, { headers });
      if (res.ok) setter(await res.json());
    } catch (e) { console.error(e); }
    setLoading(false);
  }, []);

  const fetchNotes = useCallback(async (aId, bId) => {
    if (!aId || !bId) return;
    try {
      const res = await fetch(`${API}/api/compare/notes/${aId}/${bId}`, { headers });
      if (res.ok) setNotes(await res.json());
    } catch (e) { console.error(e); }
  }, []);

  useEffect(() => { fetchAthleteData(selectedA, setDataA); }, [selectedA, fetchAthleteData]);
  useEffect(() => { fetchAthleteData(selectedB, setDataB); }, [selectedB, fetchAthleteData]);
  useEffect(() => { if (selectedA && selectedB) fetchNotes(selectedA, selectedB); }, [selectedA, selectedB, fetchNotes]);

  const saveNote = async () => {
    if (!newNote.trim() || !selectedA || !selectedB) return;
    setSavingNote(true);
    try {
      const res = await fetch(`${API}/api/compare/notes`, {
        method: 'POST', headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ athlete_a_id: selectedA, athlete_b_id: selectedB, note: newNote }),
      });
      if (res.ok) {
        toast.success('Note saved');
        setNewNote('');
        fetchNotes(selectedA, selectedB);
      }
    } catch (e) { toast.error('Failed to save note'); }
    setSavingNote(false);
  };

  const handlePrint = () => { window.print(); };

  return (
    <Layout>
      <div className="min-h-screen" ref={printRef}>
        {/* Header */}
        <div className="mb-6 flex items-center justify-between">
          <div>
            <h1 className="text-2xl sm:text-3xl font-heading uppercase text-pfap-green dark:text-gray-100 flex items-center gap-2">
              <GitCompare size={28} /> Compare Athletes
            </h1>
            <p className="text-sm text-gray-500 dark:text-gray-400 font-body mt-0.5">Side-by-side athlete evaluation</p>
          </div>
          {dataA && dataB && (
            <Button onClick={handlePrint} variant="outline" className="print:hidden gap-1.5 text-sm border-gray-200 dark:border-gray-700 dark:text-gray-300" data-testid="export-btn">
              <Printer size={16} /> Export
            </Button>
          )}
        </div>

        {/* Athlete Selectors */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6 print:hidden">
          <SearchableAthleteSelect
            athletes={athletes.filter(a => a.id !== selectedB)}
            value={selectedA}
            onChange={setSelectedA}
            placeholder="Search athlete A..."
            label="Athlete A"
            data-testid="selector-a"
          />
          <SearchableAthleteSelect
            athletes={athletes.filter(a => a.id !== selectedA)}
            value={selectedB}
            onChange={setSelectedB}
            placeholder="Search athlete B..."
            label="Athlete B"
            data-testid="selector-b"
          />
        </div>

        {loading && (
          <div className="grid grid-cols-2 gap-4 mb-6">
            {[1,2].map(i => <div key={i} className="h-64 rounded-xl bg-gray-100 dark:bg-gray-800 animate-pulse" />)}
          </div>
        )}

        {dataA && dataB && !loading && (
          <>
            {/* Basic Info */}
            <ComparisonSection title="Basic Info" icon={User}>
              <InfoRow label="Full Name" a={dataA.basic.full_name} b={dataB.basic.full_name} />
              <InfoRow label="Age" a={dataA.basic.age || '—'} b={dataB.basic.age || '—'} />
              <InfoRow label="Position" a={dataA.basic.position} b={dataB.basic.position} />
              <InfoRow label="Squad" a={dataA.basic.squad} b={dataB.basic.squad} />
              <InfoRow label="Nationality" a={dataA.basic.nationality} b={dataB.basic.nationality} />
              <InfoRow label="Status" a={dataA.basic.status} b={dataB.basic.status} />
            </ComparisonSection>

            {/* Performance */}
            <ComparisonSection title="Performance" icon={Swords}>
              <MetricRow label="Matches Played" a={dataA.performance.matches_played} b={dataB.performance.matches_played} />
              <MetricRow label="Minutes Played" a={dataA.performance.minutes_played} b={dataB.performance.minutes_played} />
              <MetricRow label="Goals" a={dataA.performance.goals} b={dataB.performance.goals} />
              <MetricRow label="Assists" a={dataA.performance.assists} b={dataB.performance.assists} />
              <MetricRow label="Avg Rating" a={dataA.performance.avg_rating} b={dataB.performance.avg_rating} max={10} />
            </ComparisonSection>

            {/* Technical */}
            <ComparisonSection title="Technical" icon={Target}>
              <MetricBar label="Pass Accuracy" a={dataA.technical.pass_accuracy} b={dataB.technical.pass_accuracy} unit="%" />
              <MetricBar label="First Touch" a={dataA.technical.first_touch} b={dataB.technical.first_touch} max={10} />
            </ComparisonSection>

            {/* Tactical */}
            <ComparisonSection title="Tactical" icon={Brain}>
              <MetricBar label="Positioning" a={dataA.tactical.positioning} b={dataB.tactical.positioning} max={10} />
              <MetricBar label="Decision Making" a={dataA.tactical.decision_making} b={dataB.tactical.decision_making} max={10} />
            </ComparisonSection>

            {/* Physical */}
            <ComparisonSection title="Physical" icon={Activity}>
              <MetricBar label="Stamina" a={dataA.physical.stamina} b={dataB.physical.stamina} max={10} />
              <MetricRow label="Avg Sprint Count" a={dataA.physical.sprint_count} b={dataB.physical.sprint_count} />
              <MetricRow label="Avg Distance (km)" a={dataA.physical.distance_covered_km} b={dataB.physical.distance_covered_km} />
            </ComparisonSection>

            {/* Mental */}
            <ComparisonSection title="Mental" icon={Brain}>
              <MetricBar label="Focus" a={dataA.mental.focus} b={dataB.mental.focus} max={10} />
              <MetricBar label="Confidence" a={dataA.mental.confidence} b={dataB.mental.confidence} max={10} />
            </ComparisonSection>

            {/* PDP */}
            <ComparisonSection title="PDP Progress" icon={ShieldCheck}>
              <InfoRow label="Current Cycle" a={dataA.pdp.cycle_name || '—'} b={dataB.pdp.cycle_name || '—'} />
              <MetricBar label="Overall Progress" a={dataA.pdp.overall_progress} b={dataB.pdp.overall_progress} unit="%" />
              <InfoRow label="Active Goals" a={dataA.pdp.goals?.length || 0} b={dataB.pdp.goals?.length || 0} />
            </ComparisonSection>

            {/* Attendance & Health */}
            <ComparisonSection title="Attendance & Health" icon={CalendarCheck}>
              <MetricBar label="Attendance Rate" a={dataA.attendance.attendance_pct} b={dataB.attendance.attendance_pct} unit="%" />
              <MetricRow label="Sessions Attended" a={`${dataA.attendance.present}/${dataA.attendance.total_sessions}`} b={`${dataB.attendance.present}/${dataB.attendance.total_sessions}`} />
              <InfoRow label="Active Injuries" a={dataA.injuries.active} b={dataB.injuries.active} />
            </ComparisonSection>

            {/* Coach Notes */}
            <Card className="bg-white dark:bg-gray-800 shadow-sm border-gray-100 dark:border-gray-700 mb-4 print:hidden" data-testid="notes-section">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
                  <Save size={16} className="text-pfap-green dark:text-gray-400" /> Comparison Notes
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex gap-2">
                  <Input
                    value={newNote}
                    onChange={e => setNewNote(e.target.value)}
                    placeholder="Add a note about this comparison..."
                    className="flex-1 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 border-gray-200 dark:border-gray-600"
                    data-testid="note-input"
                    onKeyDown={e => e.key === 'Enter' && saveNote()}
                  />
                  <Button onClick={saveNote} disabled={savingNote || !newNote.trim()} className="bg-pfap-green hover:bg-pfap-green/90 text-white dark:bg-pfap-green dark:hover:bg-pfap-green/80" data-testid="save-note-btn">
                    <Save size={16} />
                  </Button>
                </div>
                {notes.length > 0 && (
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {notes.map(n => (
                      <div key={n.id} className="text-sm p-2.5 rounded-lg bg-gray-50 dark:bg-gray-700/50 border border-gray-100 dark:border-gray-600">
                        <p className="text-gray-800 dark:text-gray-200">{n.note}</p>
                        <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">{n.created_by_name} — {new Date(n.created_at).toLocaleDateString()}</p>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </>
        )}

        {!dataA && !dataB && !loading && (
          <Card className="bg-white dark:bg-gray-800 shadow-sm border-gray-100 dark:border-gray-700">
            <CardContent className="py-16 text-center">
              <GitCompare size={48} className="mx-auto mb-4 text-gray-300 dark:text-gray-600" />
              <p className="text-gray-500 dark:text-gray-400 text-lg font-body">Select two athletes to compare</p>
              <p className="text-gray-400 dark:text-gray-500 text-sm mt-1">Choose from the dropdowns above</p>
            </CardContent>
          </Card>
        )}
      </div>
    </Layout>
  );
};

/* ==================== Sub-components ==================== */

const ComparisonSection = ({ title, icon: Icon, children }) => (
  <Card className="bg-white dark:bg-gray-800 shadow-sm border-gray-100 dark:border-gray-700 mb-4" data-testid={`section-${title.toLowerCase().replace(/\s+/g, '-')}`}>
    <CardHeader className="pb-2">
      <CardTitle className="text-sm font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
        <Icon size={16} className="text-pfap-green dark:text-gray-400" /> {title}
      </CardTitle>
    </CardHeader>
    <CardContent className="space-y-2 pt-0">{children}</CardContent>
  </Card>
);

const InfoRow = ({ label, a, b }) => (
  <div className="grid grid-cols-[1fr_120px_1fr] items-center gap-2 py-1.5 border-b border-gray-50 dark:border-gray-700/50 last:border-0">
    <span className="text-sm text-gray-800 dark:text-gray-200 text-right">{a || '—'}</span>
    <span className="text-[11px] text-gray-400 dark:text-gray-500 text-center font-medium uppercase">{label}</span>
    <span className="text-sm text-gray-800 dark:text-gray-200 text-left">{b || '—'}</span>
  </div>
);

const MetricRow = ({ label, a, b }) => {
  const numA = parseFloat(a) || 0;
  const numB = parseFloat(b) || 0;
  const diff = numA - numB;
  return (
    <div className="grid grid-cols-[1fr_120px_1fr] items-center gap-2 py-1.5 border-b border-gray-50 dark:border-gray-700/50 last:border-0">
      <div className="text-right flex items-center justify-end gap-1.5">
        <span className={`text-sm font-semibold ${numA > numB ? 'text-emerald-600 dark:text-emerald-400' : numA < numB ? 'text-gray-500 dark:text-gray-400' : 'text-gray-800 dark:text-gray-200'}`}>{a}</span>
        {diff > 0 && <ArrowUpRight size={12} className="text-emerald-500" />}
        {diff < 0 && <ArrowDownRight size={12} className="text-red-400" />}
      </div>
      <span className="text-[11px] text-gray-400 dark:text-gray-500 text-center font-medium uppercase">{label}</span>
      <div className="text-left flex items-center gap-1.5">
        {diff < 0 && <ArrowUpRight size={12} className="text-emerald-500" />}
        {diff > 0 && <ArrowDownRight size={12} className="text-red-400" />}
        <span className={`text-sm font-semibold ${numB > numA ? 'text-emerald-600 dark:text-emerald-400' : numB < numA ? 'text-gray-500 dark:text-gray-400' : 'text-gray-800 dark:text-gray-200'}`}>{b}</span>
      </div>
    </div>
  );
};

const MetricBar = ({ label, a, b, max = 100, unit = '' }) => {
  const numA = parseFloat(a) || 0;
  const numB = parseFloat(b) || 0;
  const pctA = Math.min((numA / max) * 100, 100);
  const pctB = Math.min((numB / max) * 100, 100);
  const higher = numA > numB ? 'a' : numB > numA ? 'b' : 'tied';

  return (
    <div className="py-2 border-b border-gray-50 dark:border-gray-700/50 last:border-0">
      <div className="text-[11px] text-gray-400 dark:text-gray-500 text-center font-medium uppercase mb-2">{label}</div>
      <div className="grid grid-cols-[1fr_40px_1fr] items-center gap-2">
        <div className="flex items-center gap-2">
          <span className={`text-xs font-semibold w-10 text-right ${higher === 'a' ? 'text-emerald-600 dark:text-emerald-400' : 'text-gray-500 dark:text-gray-400'}`}>
            {numA}{unit}
          </span>
          <div className="flex-1 h-2.5 rounded-full bg-gray-100 dark:bg-gray-700 overflow-hidden flex justify-end">
            <div
              className={`h-full rounded-full transition-all duration-700 ${higher === 'a' ? 'bg-pfap-green dark:bg-emerald-500' : 'bg-gray-300 dark:bg-gray-500'}`}
              style={{ width: `${pctA}%` }}
            />
          </div>
        </div>
        <div className="flex justify-center">
          {higher === 'a' && <Badge className="text-[9px] px-1 py-0 bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400 border-0">+{Math.abs(numA - numB)}{unit}</Badge>}
          {higher === 'b' && <Badge className="text-[9px] px-1 py-0 bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400 border-0">+{Math.abs(numB - numA)}{unit}</Badge>}
          {higher === 'tied' && <Minus size={12} className="text-gray-300 dark:text-gray-600" />}
        </div>
        <div className="flex items-center gap-2">
          <div className="flex-1 h-2.5 rounded-full bg-gray-100 dark:bg-gray-700 overflow-hidden">
            <div
              className={`h-full rounded-full transition-all duration-700 ${higher === 'b' ? 'bg-pfap-green dark:bg-emerald-500' : 'bg-gray-300 dark:bg-gray-500'}`}
              style={{ width: `${pctB}%` }}
            />
          </div>
          <span className={`text-xs font-semibold w-10 ${higher === 'b' ? 'text-emerald-600 dark:text-emerald-400' : 'text-gray-500 dark:text-gray-400'}`}>
            {numB}{unit}
          </span>
        </div>
      </div>
    </div>
  );
};

export default CompareAthletes;
